import '@data/Simulations/pipeline/blocks/ThresholdBlock';
import type { ThresholdParams } from '@data/Simulations/pipeline/blocks/ThresholdBlock';
import { StageExecutionMode } from '@data/Simulations/pipeline/Stage';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import RangeSlider from '@gui/components/generic/RangeSider';
import { getSentence } from '@gui/constants/sentences';
import { ControllerManager } from '@gui/controller/ControllerManager';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { Box, FormControlLabel, Radio, RadioGroup, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { BlockParameters } from '@visualizer/types';
import { useEffect, useMemo, useState, type FC } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type ThresholdParametersProps = {
    blockId: string;
};

const subSortModes = ['No', 'Rnd', 'Low1', 'Low9', 'Bary'];
const subSortGridLayout: Array<(typeof subSortModes)[number] | null> = ['No', 'Rnd', null, 'Low1', 'Low9', 'Bary'];

export const getThresholdDefaultParams = (): Pick<
    ThresholdParams,
    'thresholdRange' | 'isValueMode' | 'executionMode' | 'tolerance' | 'subSortMode'
> => ({
    thresholdRange: [0, 100],
    isValueMode: true,
    executionMode: StageExecutionMode.ALL,
    tolerance: 0,
    subSortMode: 0,
});

const ThresholdParameters: FC<ThresholdParametersProps> = ({ blockId }) => {
    const params = usePipelineBlockParams<ThresholdParams>(blockId);
    const [warnMessage, setWarnMessage] = useState('empty message');
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    const thresholdRange = params?.thresholdRange ?? [0, 100];
    const [thresholdMin, thresholdMax] = thresholdRange;
    const isValueMode = params?.isValueMode ?? true;
    const tolerance = params?.tolerance ?? 0;
    const subSortMode = params?.subSortMode ?? 0;
    const executionMode = params?.executionMode ?? StageExecutionMode.ALL;
    const hasPipelineExecuted = usePersistentStore((state) => state.getActiveLayerState().pipeline.executionCount);
    const isSecondarySortingRequired = warnMessage !== '';
    const secondarySortingLabel = isSecondarySortingRequired
        ? 'Add secondary criteria to sort Sensor Points with identical values:'
        : 'Secondary sorting, count target met.';

    const updateParams = (partial: Partial<ThresholdParams>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    useEffect(() => {
        const block = pipelineController.getBlock(blockId);
        if (!block) return;
        const notifications = block.getNotifications();
        notifications.forEach((notify) => {
            if (notify.id === 'subsort_criteria') {
                setWarnMessage(notify.message);
            }
        });
        if (notifications.length === 0) {
            setWarnMessage('');
        }
    }, [hasPipelineExecuted]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.7, mb: 0.5 }}>
                <RangeSlider
                    value={[thresholdMin, thresholdMax]}
                    lastIndex={100}
                    showValues
                    size="small"
                    step={1}
                    onChange={(next) => {
                        updateParams({ thresholdRange: next });
                    }}
                    sx={{ ml: '5px', mr: '10px', flex: 1, minWidth: 0 }}
                    sliderProps={{ 'data-testid': 'threshold-range-slider' }}
                />
                <Box>[%]</Box>
            </Box>

            <Box display="flex" justifyContent="flex-start" alignItems="center" mb={1} sx={{ gap: 3 }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={isValueMode ? 'value' : 'count'}
                    onChange={(_, next) => {
                        if (next == null) return;
                        updateParams({ isValueMode: next === 'value' });
                    }}
                    className="panel-toggle-group"
                >
                    <ToggleButton value="value">Value</ToggleButton>
                    <ToggleButton value="count">Count</ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={executionMode}
                    onChange={(_, next) => {
                        if (next == null) return;
                        updateParams({ executionMode: next });
                    }}
                    className="panel-toggle-group"
                    sx={{ mr: 2 }}
                >
                    <ToggleButton value={StageExecutionMode.ALL}>Global</ToggleButton>
                    <ToggleButton value={StageExecutionMode.BY_FLOOR}>Level</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {!isValueMode && (
                <Box sx={{ pr: 1.7 }}>
                    <CustomTooltip mode="icon" title={getSentence('Pipeline.Filters.Threshold.Tolerance.Tooltip')} side="right">
                        <Box>Value Tolerance</Box>
                    </CustomTooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Slider
                            className="panel-slider"
                            style={{ marginRight: '10px', marginLeft: '5px' }}
                            size="small"
                            value={tolerance}
                            valueLabelDisplay="on"
                            onChange={(_, v) => {
                                if (typeof v === 'number') {
                                    updateParams({ tolerance: v });
                                }
                            }}
                            step={0.1}
                            min={0}
                            max={5}
                        />
                        <Box>[%]</Box>
                    </Box>
                </Box>
            )}
            {!isValueMode && (
                <>
                    {isSecondarySortingRequired ? (
                        <Stack direction="row" sx={{ color: 'error.main', minWidth: 0 }}>
                            <Typography variant="body2" sx={{ minWidth: 0 }}>
                                {secondarySortingLabel}
                                <Box component="span" sx={{ display: 'inline-flex', ml: 0.6, verticalAlign: 'middle' }}>
                                    <CustomTooltip
                                        mode="icon"
                                        title={getSentence('Pipeline.Filters.Threshold.Sorting')}
                                        side="right"
                                        iconProps={{ style: { color: 'currentColor' } }}
                                    />
                                </Box>
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack direction="row" sx={{ color: 'text.disabled' }}>
                            <Typography variant="body2">{secondarySortingLabel}</Typography>
                        </Stack>
                    )}
                    <RadioGroup
                        name="index_subsort_mode"
                        className="panel-compact-control-stack"
                        value={subSortMode}
                        onChange={(_, v) => {
                            if (v == null) return;
                            updateParams({ subSortMode: Number(v) });
                        }}
                        sx={{
                            mt: 1,
                            mb: 1,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, max-content)',
                            columnGap: 1.2,
                            rowGap: 0.2,
                        }}
                    >
                        {subSortGridLayout.map((m, i) => {
                            if (m == null) return <Box key={`empty-${i}`} />;
                            const modeIndex = subSortModes.indexOf(m);
                            return (
                                <Box key={m}>
                                    <FormControlLabel
                                        disabled={warnMessage === ''}
                                        value={modeIndex}
                                        label={
                                            <CustomTooltip
                                                side="right"
                                                mode="icon"
                                                title={getSentence('Pipeline.Filters.Threshold.' + m + '.Tooltip')}
                                            >
                                                <span>{m}</span>
                                            </CustomTooltip>
                                        }
                                        control={<Radio size="small" />}
                                    />
                                </Box>
                            );
                        })}
                    </RadioGroup>
                </>
            )}
        </Box>
    );
};

export default ThresholdParameters;
